import { Injectable, computed, signal } from '@angular/core';
import { User } from '../models/user.model';

const USER_STORAGE_KEY = 'user';

/**
 * Signal-based store for the authenticated user.
 * Replaces the old NgRx `Store` + `CoreActions.login` pattern:
 * - `login(user)`  ≈ dispatch(CoreActions.login({ user }))
 * - `logout()`     ≈ clears persisted + in-memory session
 */
@Injectable({
  providedIn: 'root',
})
export class AuthStore {
  private readonly _user = signal<User | null>(null);

  /** Currently active user, or null when logged out. */
  readonly user = this._user.asReadonly();

  readonly isAuthenticated = computed(() => this._user() !== null);

  /** Persist the user and mark the session as authenticated. */
  login(user: User): void {
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    this._user.set(user);
  }

  /** Clear persisted + in-memory session. */
  logout(): void {
    localStorage.removeItem(USER_STORAGE_KEY);
    this._user.set(null);
  }

  /** Read the cached user from localStorage (no token validation). */
  loadStoredUser(): User | null {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as User;
    } catch (error) {
      console.error('Failed to parse stored user:', error);
      this.logout();
      return null;
    }
  }

  /** True when at least one bearer token is still valid. */
  isSessionValid(user: User): boolean {
    const now = Date.now();
    return now < user.bearerExp * 1000 || now < user.iobaseBearerExp * 1000;
  }
}
